import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, TextField, Typography} from "@mui/material";
import {InjectedEnergyLossParameter, InjectedEnergyLossUpdateItem} from "../../../../Shared/Types/InjectedEnergyLossParameter";
import {
    InjectedEnergyLossParametersReactQuery,
    isValidInjectedEnergyLossValue,
    normalizeInjectedEnergyLossValue
} from "../PowerStationReactQuery";

interface Props {
    powerStationId: number | null;
    canEdit: boolean;
    onParametersChange: (parameters: InjectedEnergyLossUpdateItem[], hasInvalidValues: boolean) => void;
}

const INVALID_VALUE_MESSAGE = "Informe um valor decimal não negativo com até seis casas decimais.";

const skidLabel = (parameter: InjectedEnergyLossParameter): string => parameter.skidName || "Sem skid";

const parameterLabel = (parameter: InjectedEnergyLossParameter): string =>
    `Perda estimada - ${skidLabel(parameter)} - ${parameter.equipmentName}`;

const meterDisplayName = (parameter: InjectedEnergyLossParameter): string =>
    `${parameter.equipmentName} - ${skidLabel(parameter)}`;

const toInputValue = (value: string | null): string => value?.replace(".", ",") ?? "";
const FIGMA_INPUT_WIDTHS = [407, 276];


const toUpdates = (
    parameters: InjectedEnergyLossParameter[],
    values: Record<number, string>
): InjectedEnergyLossUpdateItem[] => parameters.map((parameter) => ({
    equipmentId: parameter.equipmentId,
    defaultInjectedEnergyLoss: normalizeInjectedEnergyLossValue(
        values[parameter.equipmentId] ?? parameter.defaultInjectedEnergyLoss ?? ""
    )
}));

export const PowerStationInjectedEnergyLossParameters = ({
    powerStationId,
    canEdit,
    onParametersChange
}: Props) => {
    const query = InjectedEnergyLossParametersReactQuery(powerStationId);
    const [values, setValues] = useState<Record<number, string>>({});
    const valuesRef = useRef<Record<number, string>>({});
    const previousPowerStationId = useRef(powerStationId);

    const emitParametersChange = useCallback((
        parameters: InjectedEnergyLossParameter[],
        nextValues: Record<number, string>
    ) => {
        const hasInvalidValues = parameters.some((parameter) => !isValidInjectedEnergyLossValue(
            nextValues[parameter.equipmentId] ?? parameter.defaultInjectedEnergyLoss ?? ""
        ));

        onParametersChange(
            hasInvalidValues ? [] : toUpdates(parameters, nextValues),
            hasInvalidValues
        );
    }, [onParametersChange]);

    useEffect(() => {
        if (!query.data) {
            return;
        }

        const resetValues = previousPowerStationId.current !== powerStationId;
        previousPowerStationId.current = powerStationId;
        const source = resetValues ? {} : valuesRef.current;
        const nextValues: Record<number, string> = {};

        query.data.forEach((parameter) => {
            nextValues[parameter.equipmentId] = Object.prototype.hasOwnProperty.call(source, parameter.equipmentId)
                ? source[parameter.equipmentId]
                : toInputValue(parameter.defaultInjectedEnergyLoss);
        });

        valuesRef.current = nextValues;
        setValues(nextValues);
        emitParametersChange(query.data, nextValues);
    }, [emitParametersChange, powerStationId, query.data]);

    const handleValueChange = (parameter: InjectedEnergyLossParameter, value: string) => {
        if (!query.data) {
            return;
        }

        const nextValues = {
            ...valuesRef.current,
            [parameter.equipmentId]: value
        };

        valuesRef.current = nextValues;
        setValues(nextValues);
        emitParametersChange(query.data, nextValues);
    };

    if (powerStationId === null) {
        return null;
    }

    const parameters = query.data ?? [];
    const columnBreak = Math.ceil(parameters.length / 2);
    const parameterColumns = [parameters.slice(0, columnBreak), parameters.slice(columnBreak)]
        .filter((column) => column.length > 0);

    return (
        <Box
            component="section"
            aria-labelledby="injected-energy-loss-title"
            sx={{mt: 3, pr: {xs: 0, sm: 2}, width: "100%"}}
        >
            <Box sx={{display: "flex", flexDirection: "column", gap: 0.5, mb: 3}}>
                <Typography
                    component="h3"
                    id="injected-energy-loss-title"
                    sx={{m: 0, fontSize: 16, fontWeight: 500, lineHeight: "24px"}}
                >
                    Parâmetros de correção de energia injetada
                </Typography>
                <Typography color="text.secondary" sx={{fontSize: 14, lineHeight: "20px"}}>
                    Informe o valor de perda estimada para cada medidor. Se o campo permanecer vazio, o sistema considerará que não há perda configurada.
                </Typography>
            </Box>

            {query.isLoading && <Typography role="status">Carregando medidores de energia...</Typography>}
            {query.isError && <Typography role="alert">Não foi possível carregar os medidores de energia.</Typography>}
            {!query.isLoading && !query.isError && parameters.length === 0 && (
                <Typography>Nenhum medidor de energia cadastrado</Typography>
            )}

            {!query.isLoading && !query.isError && parameters.length > 0 && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))"},
                        columnGap: 6,
                        rowGap: 3
                    }}
                >
                    {parameterColumns.map((column, columnIndex) => (
                        <Box
                            key={column[0].equipmentId}
                            role="group"
                            aria-label={`Coluna ${columnIndex + 1} de medidores`}
                            sx={{minWidth: 0}}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    color: "text.secondary",
                                    fontSize: 12,
                                    fontWeight: 500,
                                    lineHeight: "16px"
                                }}
                            >
                                <Typography component="span" sx={{font: "inherit", color: "inherit"}}>
                                    Medidor de Energia
                                </Typography>
                                <Typography component="span" sx={{font: "inherit", color: "inherit"}}>
                                    Valor de perda estimada
                                </Typography>
                            </Box>

                            {column.map((parameter) => {
                                const rawValue = values[parameter.equipmentId] ?? "";
                                const fieldError = isValidInjectedEnergyLossValue(rawValue)
                                    ? undefined
                                    : INVALID_VALUE_MESSAGE;
                                const fieldErrorId = `injected-energy-loss-error-${parameter.equipmentId}`;

                                return (
                                    <Box
                                        key={parameter.equipmentId}
                                        sx={{
                                            display: "flex",
                                            flexDirection: {xs: "column", lg: "row"},
                                            alignItems: {xs: "stretch", lg: "center"},
                                            justifyContent: "space-between",
                                            gap: {xs: 1, lg: 2},
                                            minHeight: 56,
                                            py: 1,
                                            boxSizing: "border-box"
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                py: {xs: 0, lg: 0.5},
                                                fontSize: 16,
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                                letterSpacing: "0.15px"
                                            }}
                                        >
                                            {meterDisplayName(parameter)}
                                        </Typography>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            error={fieldError !== undefined}
                                            helperText={fieldError}
                                            FormHelperTextProps={{
                                                id: fieldErrorId,
                                                role: "alert"
                                            }}
                                            sx={{
                                                width: {xs: "100%", lg: FIGMA_INPUT_WIDTHS[columnIndex]},
                                                flexShrink: 0,
                                                "& .MuiOutlinedInput-root": {
                                                    height: 40,
                                                    bgcolor: "grey.50"
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "divider"
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    px: 1.5,
                                                    py: "9px",
                                                    fontSize: 14,
                                                    lineHeight: "20px"
                                                }
                                            }}
                                            inputProps={{
                                                "aria-label": parameterLabel(parameter),
                                                "aria-describedby": fieldError === undefined ? undefined : fieldErrorId,
                                                "aria-invalid": fieldError === undefined ? undefined : true,
                                                inputMode: "decimal"
                                            }}
                                            value={rawValue}
                                            disabled={!canEdit}
                                            onChange={(event) => handleValueChange(parameter, event.target.value)}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
